import { firebaseFunctions } from "./firebaseApp";
import { httpsCallable } from "firebase/functions";

/*
 * Callable wrappers for a gym's terms & conditions.
 *
 * Every one of these is server-owned: Firestore rules deny clients the whole
 * PublishedTerms tree, so there is no read-it-directly shortcut to fall back on
 * and no second way for these shapes to reach the UI.
 *
 * Errors propagate. The server's refusals are written to be shown to a gym
 * owner — an over-limit body names the limit, a disallowed character names its
 * position — and only the calling surface knows where to put that text.
 */

/**
 * Publish a new version of this gym's terms.
 *
 * Idempotent on identical bytes: re-publishing unchanged text returns the live
 * version with `idempotent: true` rather than minting a new one, so a
 * double-click does not create a second legal instrument.
 *
 * @param {{boxID: string, markdown: string, locale: "en"|"fi"}} payload
 * @return {Promise<Object>} PublishGymTermsResponse
 */
export const publishGymTerms = async (payload) => {
  const publish = httpsCallable(firebaseFunctions, "publishGymTerms");
  const ret = await publish(payload);
  return ret.data;
};

/**
 * Stop publishing this gym's terms. Every archived version is kept.
 *
 * @param {string} boxID
 * @return {Promise<Object>} WithdrawGymTermsResponse
 */
export const withdrawGymTerms = async (boxID) => {
  const withdraw = httpsCallable(firebaseFunctions, "withdrawGymTerms");
  const ret = await withdraw({ boxID });
  return ret.data;
};

/**
 * The authoring view: what is live, the version history, and the public links.
 *
 * @param {string} boxID
 * @param {string} [cursor] Version tag of the previous page's last row.
 * @return {Promise<Object>} GetGymTermsForAdminResponse
 */
export const getGymTermsForAdmin = async (boxID, cursor) => {
  const forAdmin = httpsCallable(firebaseFunctions, "getGymTermsForAdmin");
  const ret = await forAdmin(cursor ? { boxID, cursor } : { boxID });
  return ret.data;
};

/**
 * Whether a gym has terms and where to read them.
 *
 * Takes exactly one selector — a boxID from a cart line, or the caller's own
 * subscription id, which the server resolves to the gym. Sending both is
 * refused rather than silently reconciled.
 *
 * @param {{boxID: string}|{subscriptionId: string}} selector
 * @return {Promise<Object>} GymTermsSummary
 */
export const getGymTermsSummary = async (selector) => {
  const summary = httpsCallable(firebaseFunctions, "getGymTermsSummary");
  const ret = await summary(selector);
  return ret.data;
};

/**
 * A gym's published terms, readable without an account.
 *
 * @param {{pageName: string}|{boxID: string}} selector Plus optional `version`.
 * @return {Promise<Object>} GymTermsPageResponse
 */
export const getGymTermsPage = async (selector) => {
  const page = httpsCallable(firebaseFunctions, "getGymTermsPage");
  const ret = await page(selector);
  return ret.data;
};
